import type { Member } from "../../types";
import { formatEntryDate, formatEntryDateTime } from "../../lib/format";
import { Button } from "../Button/Button";
import { IconStore } from "../icons/icons";
import styles from "./MemberTable.module.css";

interface MemberTableProps {
  tenantName: string;
  members: Member[];
  hasMore: boolean;
  loadingMore: boolean;
  loadingFirstPage: boolean;
  onLoadMore: () => void;
}

export function MemberTable({
  tenantName,
  members,
  hasMore,
  loadingMore,
  loadingFirstPage,
  onLoadMore,
}: MemberTableProps) {
  if (loadingFirstPage) {
    return (
      <div className={styles.wrap}>
        <div className={styles.empty} role="status" aria-live="polite">
          Loading {tenantName}'s members…
        </div>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className={styles.wrap}>
        <div className={styles.empty}>
          <IconStore className={styles.emptyIcon} />
          <p>No one has joined {tenantName} yet. Share the signup link or QR code above to get your first member.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <div
        className={styles.scroller}
        tabIndex={0}
        aria-label={`${tenantName} members; scroll horizontally for more columns`}
      >
        <table className={styles.table}>
          <caption className="visually-hidden">{tenantName}'s members</caption>
          <thead>
            <tr>
              <th scope="col">First name</th>
              <th scope="col">Last name</th>
              <th scope="col">Email</th>
              <th scope="col">Joined</th>
              <th scope="col">Network offers</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.memberId}>
                <td>{member.firstName}</td>
                <td>{member.lastName}</td>
                <td>{member.email}</td>
                <td className="tabular-nums" title={formatEntryDateTime(member.joinedAt)}>
                  {formatEntryDate(member.joinedAt)}
                </td>
                <td className={member.networkOffers ? styles.consentYes : styles.consentNo}>
                  {member.networkOffers ? "Yes" : "No"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles.footer}>
        <span className={styles.count} role="status" aria-live="polite">
          {hasMore
            ? `Showing ${members.length} ${members.length === 1 ? "member" : "members"}.`
            : `Showing all ${members.length} ${members.length === 1 ? "member" : "members"}.`}
        </span>
        {hasMore && (
          <Button variant="secondary" busy={loadingMore} onClick={onLoadMore}>
            Show next 50 members
          </Button>
        )}
      </div>
    </div>
  );
}
